import React from 'react';
import { RequirementsProps } from '../../tools/context';
import { imageUrlFor } from '../../tools/sanity';
import { LinkProps } from '../index';
import Logo from '../Logo';
import { Picto } from '../Picto';
import { FacebookLogo, InstagramLogo, YoutubeLogo } from '../Picto/charentes/Icons';

export interface ImageProps {
  media: any;
  link?: string;
}

export interface SocialProps {
  image: any;
  link?: string;
  alt?: string;
}

interface ContentItem {
  _key?: number;
  type: 'text' | 'image';
  text?: {
    content: string;
    hasLink: boolean;
    link?: string;
  };
  image?: any;
  imageLink?: string;
}

interface SubHeroSection {
  left: ContentItem[];
  middle: ContentItem[];
  right: ContentItem[];
}

interface MainSection {
  tags?: FooterTag[];
  helpLink2Url: string;
  proLink2Text: string;
  proLink1Url: string;
  proLink1Text: string;
  helpLink2Text: string;
  helpTitle: string;
  facebookLink: string;
  instagramLink: string;
  youtubeLink: string;
  proTitle: string;
  helpLink1Url: string;
  proLink2Url: string;
  helpLink1Text: string;
  helpLink1Background?: { hex: string };
  helpLink2Background?: { hex: string };
  proLink1Background?: { hex: string };
  proLink2Background?: { hex: string };
  legalLink1Text?: string;
  legalLink1Url?: string;
  legalLink1Background?: { hex: string };
  legalLink2Text?: string;
  legalLink2Url?: string;
  legalLink2Background?: { hex: string };
  legalLink3Text?: string;
  legalLink3Url?: string;
  legalLink3Background?: { hex: string };
  legalLink4Text?: string;
  legalLink4Url?: string;
  legalLink4Background?: { hex: string };
}

type FooterColumn = 'col1' | 'col2' | 'col3' | 'col4';

interface FooterTagLink {
  text?: string;
  link?: string;
  external?: boolean;
  picto?: string;
}

interface FooterTag {
  _key?: string;
  column: FooterColumn;
  backgroundColor?: { hex: string } | string;
  link: FooterTagLink;
}

export interface FooterProps {
  footer?: {
    navigation?: LinkProps[];
    bottomLinks?: LinkProps[];
    rightLogo?: ImageProps;
    leftLogoLink?: string;
    socialList?: SocialProps[];
    subHero?: SubHeroSection;
    mainSection?: MainSection;
    type?: 'classic' | 'modern';
  };
  footerType?: 'classic' | 'modern';
  requirements: RequirementsProps;
}

const BuildVersion = () => {
  const date = process.env.REACT_APP_BUILD_DATE;
  const commit = process.env.REACT_APP_COMMIT_ID;
  if (!date && !commit) return null;
  return (
    <div
      style={{
        textAlign: 'right',
        padding: '8px 16px 4px',
        fontSize: '10px',
        color: 'rgba(128,128,128,0.4)',
        fontFamily: 'monospace',
      }}
    >
      {date}
      {commit ? ` · ${commit}` : ''}
    </div>
  );
};

export const Footer = ({ footer, requirements, footerType }: FooterProps) => {
  // console.log('Footer props received:', { footer, requirements, footerType });

  return footerType === 'classic' ? (
    <ClassicFooter footer={footer} requirements={requirements} />
  ) : (
    <ModernFooter footer={footer} requirements={requirements} />
  );
};

export const ClassicFooter = ({ footer, requirements }: FooterProps) => {
  const links = footer && footer.navigation ? footer.navigation : [];
  const bottomLinks = footer && footer.bottomLinks ? footer.bottomLinks : [];
  const rightLogo = footer && footer.rightLogo ? footer.rightLogo : null;
  const socialList = footer && footer.socialList ? footer.socialList : [];

  const RightLogoComp = rightLogo?.link ? 'a' : 'div';
  const RightLogoProps = rightLogo?.link
    ? { href: rightLogo.link, target: '_blank', rel: 'noopener' }
    : {};
  const logoLink = footer?.leftLogoLink || '/';

  return (
    <footer className={'Footer'} role="contentinfo">
      <div className="footer-content">
        <div className="footer-logo">
          <Logo
            location={'footer'}
            requirements={requirements}
            logoLink={logoLink}
            target_blank={true}
          />
          <div className="social-images-row">
            {socialList.map((social, index) => (
              <a
                key={index}
                className="social-image"
                href={social.link ? social.link : '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.alt ? `${social.alt} (ouvre un nouvel onglet)` : `Réseau social (ouvre un nouvel onglet)`}
              >
                <img
                  src={imageUrlFor(social.image, requirements).url()}
                  alt=""
                  aria-hidden="true"
                />
              </a>
            ))}
          </div>
        </div>
        <nav aria-label="Liens du pied de page">
          <div className="links">
            {links.map((link, index) => (
              <a
                className={'link'}
                key={index}
                href={link.link}
                target={link.external ? '_blank' : null}
                rel={link.external ? 'noreferrer' : undefined}
                aria-label={link.external ? `${link.text} (ouvre un nouvel onglet)` : undefined}
              >
                {link.text}
              </a>
            ))}
          </div>
        </nav>
        <div className="footer-logo">
          {rightLogo && rightLogo.media && (
            <RightLogoComp className="block" key="test" {...RightLogoProps}>
              <img src={imageUrlFor(rightLogo.media, requirements).url()} alt="Right footer logo" />
            </RightLogoComp>
          )}
        </div>
      </div>
      <div className="footer-bottom">
        {bottomLinks.map((link, index) => (
          <React.Fragment key={index}>
            <a className={`link ${link.link ? 'url' : ''}`} href={link.link}>
              {link.text}
            </a>
            <div className="dot" aria-hidden="true" />
          </React.Fragment>
        ))}
        <a href="/">
          {/* <a className={`link url`} href="javascript:openAxeptioCookies()"> */}
          Cookies
        </a>
      </div>
      <BuildVersion />
    </footer>
  );
};

export const ModernFooter = ({ footer, requirements }: FooterProps) => {
  const logoLink = footer?.leftLogoLink || '/';
  const middleSection = footer?.subHero.middle || [];
  const rightSection = footer?.subHero.right || [];
  const leftSection = footer?.subHero.left || [];
  const mainSection: MainSection = footer?.mainSection || ({} as MainSection);

  const footerTags = mainSection?.tags || [];
  const hasNewFooterTags = Array.isArray(footerTags) && footerTags.length > 0;
  const getTagBackgroundColor = (tag: FooterTag) => tag.backgroundColor && 'hex' in (tag.backgroundColor as any) ? (tag.backgroundColor as any)?.hex : (tag.backgroundColor as any);

  const col1Tags = footerTags.filter((t) => t.column === 'col1');
  const col2Tags = footerTags.filter((t) => t.column === 'col2');
  const col3Tags = footerTags.filter((t) => t.column === 'col3');
  const col4Tags = footerTags.filter((t) => t.column === 'col4');

  const helpLink1Background =
    (mainSection as any)?.helpLink1Background?.hex ||
    (mainSection as any)?.helpLink1Background ||
    '#96c0e8';
  const helpLink2Background =
    (mainSection as any)?.helpLink2Background?.hex ||
    (mainSection as any)?.helpLink2Background ||
    '#e8f5c8';
  const proLink1Background =
    (mainSection as any)?.proLink1Background?.hex ||
    (mainSection as any)?.proLink1Background ||
    '#ffd2a3';
  const proLink2Background =
    (mainSection as any)?.proLink2Background?.hex ||
    (mainSection as any)?.proLink2Background ||
    '#ffd4e6';

  const legalCards = [
    {
      text: mainSection?.legalLink1Text || 'Mentions légales',
      url: mainSection?.legalLink1Url || '/mentions-legales',
      background:
        (mainSection as any)?.legalLink1Background?.hex ||
        (mainSection as any)?.legalLink1Background ||
        '#e2dcff',
    },
    {
      text: mainSection?.legalLink2Text || 'Politique de confidentialité',
      url: mainSection?.legalLink2Url || '/politique-de-confidentialite',
      background:
        (mainSection as any)?.legalLink2Background?.hex ||
        (mainSection as any)?.legalLink2Background ||
        '#e5e5e5',
    },
    {
      text: mainSection?.legalLink3Text || 'Accessibilité',
      url: mainSection?.legalLink3Url || '/mentions-accessibilite',
      background:
        (mainSection as any)?.legalLink3Background?.hex ||
        (mainSection as any)?.legalLink3Background ||
        '#d7f3ff',
    },
    {
      text: mainSection?.legalLink4Text || 'Brochures',
      url: mainSection?.legalLink4Url || '/telechargez-nos-brochures',
      background:
        (mainSection as any)?.legalLink4Background?.hex ||
        (mainSection as any)?.legalLink4Background ||
        '#fff1cc',
    },
  ];

  const legalCardsCol3 = legalCards.slice(0, 2);
  const legalCardsCol4 = legalCards.slice(2, 4);

  return (
    <footer className="ModernFooter" role="contentinfo">
      <div className="main-footer">
        <div className="logo-section">
          <Logo
            location="footer"
            requirements={requirements}
            logoLink={logoLink}
            target_blank={false}
          />
          <div className="social-links">
            <a href={mainSection?.facebookLink || '#'} target="_blank" rel="noreferrer" aria-label="Facebook (ouvre un nouvel onglet)">
              <FacebookLogo aria-hidden="true" />
            </a>
            <a href={mainSection?.instagramLink || '#'} target="_blank" rel="noreferrer" aria-label="Instagram (ouvre un nouvel onglet)">
              <InstagramLogo aria-hidden="true" />
            </a>
            <a href={mainSection?.youtubeLink || '#'} target="_blank" rel="noreferrer" aria-label="YouTube (ouvre un nouvel onglet)">
              <YoutubeLogo aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="cards-section">
          {hasNewFooterTags ? (
            <>
              <div className="help-section">
                <span>{mainSection?.helpTitle || 'Aide'}</span>
                {col1Tags.map((tag, index) => (
                  <a
                    href={tag.link?.link || '#'}
                    target={tag.link?.external ? '_blank' : undefined}
                    rel={tag.link?.external ? 'noreferrer' : undefined}
                    className="legal-card"
                    key={tag._key || index}
                    style={{ backgroundColor: getTagBackgroundColor(tag) }}
                  >
                    {tag.link?.picto ? <Picto iconKey={tag.link.picto} height="18" width="18" /> : null}
                    {tag.link?.text || ''}
                  </a>
                ))}
              </div>

              <div className="pro-section">
                <span>{mainSection?.proTitle || 'Accès professionnels'}</span>
                {col2Tags.map((tag, index) => (
                  <a
                    href={tag.link?.link || '#'}
                    target={tag.link?.external ? '_blank' : undefined}
                    rel={tag.link?.external ? 'noreferrer' : undefined}
                    className="legal-card"
                    key={tag._key || index}
                    style={{ backgroundColor: getTagBackgroundColor(tag) }}
                  >
                    {tag.link?.picto ? <Picto iconKey={tag.link.picto} height="18" width="18" /> : null}
                    {tag.link?.text || ''}
                  </a>
                ))}
              </div>

              <div className="legal-section">
                <span className="legal-title-spacer" aria-hidden="true" />
                {col3Tags.map((tag, index) => (
                  <a
                    href={tag.link?.link || '#'}
                    target={tag.link?.external ? '_blank' : undefined}
                    rel={tag.link?.external ? 'noreferrer' : undefined}
                    className="legal-card"
                    key={tag._key || index}
                    style={{ backgroundColor: getTagBackgroundColor(tag) }}
                  >
                    {tag.link?.picto ? <Picto iconKey={tag.link.picto} height="18" width="18" /> : null}
                    {tag.link?.text || ''}
                  </a>
                ))}
              </div>

              <div className="accessibility-section">
                <span className="legal-title-spacer" aria-hidden="true" />
                {col4Tags.map((tag, index) => (
                  <a
                    href={tag.link?.link || '#'}
                    target={tag.link?.external ? '_blank' : undefined}
                    rel={tag.link?.external ? 'noreferrer' : undefined}
                    className="legal-card"
                    key={tag._key || index}
                    style={{ backgroundColor: getTagBackgroundColor(tag) }}
                  >
                    {tag.link?.picto ? <Picto iconKey={tag.link.picto} height="18" width="18" /> : null}
                    {tag.link?.text || ''}
                  </a>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="help-section">
                <span>{mainSection?.helpTitle || 'Aide'}</span>
                <a
                  href={mainSection?.helpLink1Url || '/contact'}
                  className="help-link contact-link"
                  style={{ backgroundColor: helpLink1Background }}
                >
                  <span className="icon">📧 </span>
                  {mainSection?.helpLink1Text || 'Contact'}
                </a>
                <a
                  href={mainSection?.helpLink2Url || '/offices'}
                  className="help-link office-link"
                  style={{ backgroundColor: helpLink2Background }}
                >
                  <span className="icon">🧭 </span>
                  {mainSection?.helpLink2Text || 'Office de Tourisme'}
                </a>
              </div>

              <div className="pro-section">
                <span>{mainSection?.proTitle || 'Accès professionnels'}</span>
                <a
                  href={mainSection?.proLink1Url || '/espace-professionnel'}
                  className="pro-link footer-pro-link"
                  style={{ backgroundColor: proLink1Background }}
                >
                  <span className="icon">💼 </span>
                  {mainSection?.proLink1Text || 'Espace Pro'}
                </a>
                <a
                  href={mainSection?.proLink2Url || '/espace-presse'}
                  className="pro-link footer-press-link"
                  style={{ backgroundColor: proLink2Background }}
                >
                  <span className="icon">📰 </span>
                  {mainSection?.proLink2Text || 'Presse'}
                </a>
              </div>

              <div className="legal-section">
                <span className="legal-title-spacer" aria-hidden="true" />
                {legalCardsCol3.map((item, index) => (
                  <a
                    href={item.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="legal-card"
                    key={index}
                    style={{ backgroundColor: item.background }}
                  >
                    <span className="icon">{index === 0 ? '⚖️ ' : '🔐 '}</span>
                    {item.text}
                  </a>
                ))}
              </div>

              <div className="accessibility-section">
                <span className="legal-title-spacer" aria-hidden="true" />
                {legalCardsCol4.map((item, index) => (
                  <a
                    href={item.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="legal-card"
                    key={index}
                    style={{ backgroundColor: item.background }}
                  >
                    <span className="icon">{index === 0 ? '🫆 ' : '📘 '}</span>
                    {item.text}
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bottom-footer">
        <div className="copyright" style={{ textAlign: 'left' }}>
          {leftSection.map((item, index) => (
            <span key={index}>{item.text?.content}</span>
          ))}
        </div>

        <div className="logos" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          {middleSection.map((item, index) => (
            <a href={item?.imageLink} target="_blank" rel="noreferrer" key={item?._key || index}>
              <img
                src={imageUrlFor(item.image, requirements).url()}
                alt={item.text?.content}
                style={{ width: '85px', height: 'auto' }}
              />
            </a>
          ))}
        </div>

        <div className="legal-links">
          {rightSection.map((item, index) => (
            <a
              href={item.text?.link}
              target="_blank"
              rel="noreferrer"
              className="legal-link"
              key={item?._key || index}
              aria-label={item.text?.content ? `${item.text.content} (ouvre un nouvel onglet)` : undefined}
            >
              {item.text?.content}
            </a>
          ))}
        </div>
      </div>
      <BuildVersion />
    </footer>
  );
};
